import React from 'react';
import { Document } from 'react-pdf';
import { LinearProgress } from '@mui/material';
import type { OnDocumentLoadSuccess } from 'react-pdf/dist/cjs/shared/types';

import { usePdfLoadErrorHandler } from '@/hooks/usePdfLoadErrorHandler';
import '@/utils/pdfSetup';

import { ESignPdfPage } from './ESignPdfPage';
import type { ESignPageDimensions } from './types';
import { useESignPdfDocument } from './useESignPdfDocument';

const DEFAULT_PDF_ASPECT_RATIO = 11 / 8.5;

interface ESignPdfViewerProps {
  url: string | null | undefined;
  width: number;
  onUrlExpired?: () => void | Promise<void>;
  renderOverlay?: (
    pageNumber: number,
    dimensions: ESignPageDimensions
  ) => React.ReactNode;
  onDocumentLoad?: (numPages: number) => void;
}

export function ESignPdfViewer({
  url,
  width,
  onUrlExpired,
  renderOverlay,
  onDocumentLoad
}: ESignPdfViewerProps) {
  const {
    activeUrl,
    pdfKey,
    numPages,
    isLoading,
    pageDimensionsMap,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    onPageRenderSuccess,
    errorRetryCountRef
  } = useESignPdfDocument({ url });

  const handleDocumentLoadError = usePdfLoadErrorHandler({
    errorRetryCountRef,
    onPdfLoadError: onDocumentLoadError,
    onUrlExpired
  });

  const handleDocumentLoadSuccess: OnDocumentLoadSuccess = React.useCallback(
    (pdf) => {
      onDocumentLoadSuccess(pdf);
      onDocumentLoad?.(pdf.numPages);
    },
    [onDocumentLoadSuccess, onDocumentLoad]
  );

  // Map iteration follows onRenderSuccess order, not page number.
  const fallbackAspectRatio = React.useMemo<number>(() => {
    const firstPage =
      pageDimensionsMap.get(1) ?? pageDimensionsMap.values().next().value;
    if (firstPage && firstPage.renderedWidth > 0) {
      return firstPage.renderedHeight / firstPage.renderedWidth;
    }
    return DEFAULT_PDF_ASPECT_RATIO;
  }, [pageDimensionsMap]);

  if (!url) {
    return null;
  }

  return (
    <Document
      key={pdfKey}
      file={activeUrl}
      loading={<LinearProgress />}
      onLoadSuccess={handleDocumentLoadSuccess}
      onLoadError={handleDocumentLoadError}
    >
      {!isLoading &&
        Array.from({ length: numPages }, (_, i) => {
          const pageNumber = i + 1;
          return (
            <ESignPdfPage
              key={pageNumber}
              pageNumber={pageNumber}
              width={width}
              onRenderSuccess={onPageRenderSuccess}
              pageDimensions={pageDimensionsMap.get(pageNumber)}
              fallbackAspectRatio={fallbackAspectRatio}
              renderOverlay={renderOverlay}
            />
          );
        })}
    </Document>
  );
}
